import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { getNotifications, markNotificationRead, type Notification } from '@/api/notifications'

const POLL_MS = 20000
const MARQUEE_TYPES = ['customer_order_created', 'customer_order_edited']
const PX_PER_SECOND = 60

export default function NotificationMarquee() {
  const navigate = useNavigate()
  const [items, setItems] = useState<Notification[]>([])
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [paused, setPaused] = useState(false)
  const [trackWidth, setTrackWidth] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  const refresh = useCallback(() => {
    getNotifications(true)
      .then(res => setItems(res.data.filter(n => MARQUEE_TYPES.includes(n.type))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, POLL_MS)
    return () => clearInterval(timer)
  }, [refresh])

  const visible = items.filter(n => !dismissed.has(n.id))

  useEffect(() => {
    if (trackRef.current) setTrackWidth(trackRef.current.scrollWidth)
  }, [visible.length])

  if (visible.length === 0) return null

  const handleClick = async (n: Notification) => {
    try { await markNotificationRead(n.id) } catch { /* ignore */ }
    setDismissed(prev => new Set(prev).add(n.id))
    if (n.order_id) navigate(`/orders?editOrder=${n.order_id}`)
  }

  const handleDismiss = async (e: React.MouseEvent, n: Notification) => {
    e.stopPropagation()
    try { await markNotificationRead(n.id) } catch { /* ignore */ }
    setDismissed(prev => new Set(prev).add(n.id))
  }

  const renderChips = (keyPrefix: string) => visible.map(n => (
    <button
      key={`${keyPrefix}-${n.id}`}
      type="button"
      onClick={() => handleClick(n)}
      className="inline-flex items-center gap-1.5 shrink-0 rounded-full bg-amber-100 dark:bg-amber-800/50 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800 transition-colors cursor-pointer"
    >
      <span>{n.title}</span>
      <span
        role="button"
        onClick={(e) => handleDismiss(e, n)}
        className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-100"
      >
        <X className="h-3 w-3" />
      </span>
    </button>
  ))

  const duration = trackWidth > 0 ? trackWidth / PX_PER_SECOND : 10

  return (
    <div
      className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes notification-marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
      <div className="flex items-center gap-2 px-3 py-1.5">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <div className="overflow-hidden flex-1">
          <div
            className="flex items-center gap-2 whitespace-nowrap w-max"
            style={{
              animation: `notification-marquee-scroll ${duration}s linear infinite`,
              animationPlayState: paused ? 'paused' : 'running',
            }}
          >
            <div ref={trackRef} className="flex items-center gap-2 shrink-0">
              {renderChips('a')}
            </div>
            <div className="flex items-center gap-2 shrink-0" aria-hidden="true">
              {renderChips('b')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
